import {getSpreadsheetModelById} from "./aiEnrichment";
import {formatCreditCount} from "./creditBalanceModel";

export const ACTION_CREDIT_SUCCESS_NOTE = "Only successful results are charged.";
const PER_COMPANY_CREDIT_ACTIONS = new Set(["find_email", "verify_email", "find_and_verify_email"]);

export function getMaxCreditsPerUnit(actionId, {aiModelId, creditsPerUnit} = {}) {
  if (!actionId) return 0;
  if (Number.isFinite(creditsPerUnit) && creditsPerUnit > 0) return creditsPerUnit;
  if (actionId === "ai_enrichment") {
    if (!aiModelId) return 0;
    return getSpreadsheetModelById(aiModelId)?.credits ?? 0;
  }
  return PER_COMPANY_CREDIT_ACTIONS.has(actionId) ? 1 : 0;
}

export function buildActionCreditEstimate({
  actionId,
  selectedCount = 0,
  availableCredits = null,
  aiModelId,
  creditsPerUnit,
} = {}) {
  // Client price ceiling only; backend reservation and 402 refusal stay authoritative.
  const upperBoundCreditsPerUnit = getMaxCreditsPerUnit(actionId, {aiModelId, creditsPerUnit});
  const safeCount = Math.max(0, Number(selectedCount) || 0);
  const maxCreditsUsed = upperBoundCreditsPerUnit * safeCount;
  const hasBalance = Number.isFinite(availableCredits);
  const difference = hasBalance ? availableCredits - maxCreditsUsed : null;
  const hasShortfall = difference !== null && difference < 0;
  const estimatedRemaining = difference !== null && !hasShortfall ? difference : null;
  const creditShortfall = hasShortfall ? Math.abs(difference) : 0;

  return {
    actionId,
    selectedCount: safeCount,
    creditsPerUnit: upperBoundCreditsPerUnit,
    availableCredits: hasBalance ? availableCredits : null,
    maxCreditsUsed,
    estimatedRemaining,
    creditShortfall,
    hasShortfall,
    availableLabel: hasBalance ? formatCreditCount(availableCredits) : null,
    maxLabel: formatCreditCount(maxCreditsUsed),
    remainingLabel: estimatedRemaining === null ? null : formatCreditCount(estimatedRemaining),
    shortfallLabel: hasShortfall ? formatCreditCount(creditShortfall) : null,
    showEstimate: Boolean(actionId) && safeCount > 0 && upperBoundCreditsPerUnit > 0,
  };
}

export function isCreditBalanceResolved({
  availableCredits,
  loading = false,
  refreshing = false,
  error = null,
} = {}) {
  return !loading && !refreshing && !error && Number.isFinite(availableCredits);
}

export function formatSelectionCountLabel(selectedCount) {
  const count = Math.max(0, Number(selectedCount) || 0);
  return `${count} ${count === 1 ? "company" : "companies"}`;
}

export function formatMaxCreditsCostLabel(maxCreditsUsed) {
  const max = Math.max(0, Number(maxCreditsUsed) || 0);
  return `Up to ${formatCreditCount(max)} ${max === 1 ? "credit" : "credits"}`;
}

export function formatMaxCreditsWillBeUsedLabel(maxCreditsUsed) {
  return `${formatMaxCreditsCostLabel(maxCreditsUsed)} will be used`;
}

export function formatCompactCreditUsageSummary(estimate) {
  if (!estimate?.showEstimate) return null;
  const willUseLabel = formatMaxCreditsWillBeUsedLabel(estimate.maxCreditsUsed);
  const remainingSuffix = estimate.hasShortfall
    ? `${estimate.shortfallLabel} ${estimate.creditShortfall === 1 ? "credit" : "credits"} short`
    : estimate.remainingLabel !== null
      ? `${estimate.remainingLabel} credits remaining`
      : null;
  return {
    selectionLabel: formatSelectionCountLabel(estimate.selectedCount),
    costLabel: formatMaxCreditsCostLabel(estimate.maxCreditsUsed),
    willUseLabel,
    statsLead: willUseLabel,
    remainingSuffix,
    statsLine: remainingSuffix ? `${willUseLabel} · ${remainingSuffix}` : willUseLabel,
  };
}

// FE #748: the cost a paid grid button may print. Only a guaranteed ceiling becomes a
// number: Find Verified Emails charges at most 1 credit per company (BE charged_units <= 1).
// Any other client estimate could undercount the backend quote, so it never becomes a number.
const CEILING_CREDITS_PER_UNIT = {find_and_verify_email: 1};

export function paidButtonCostSuffix({actionId, unitCount = 0, confirmFollows = false} = {}) {
  if (!actionId) return "";
  const perUnit = CEILING_CREDITS_PER_UNIT[actionId];
  if (perUnit && unitCount > 0) {
    const ceiling = perUnit * unitCount;
    return `up to ${formatCreditCount(ceiling)} ${ceiling === 1 ? "credit" : "credits"}`;
  }
  return confirmFollows ? "cost shown before you confirm" : "";
}
